import { select, call, takeLatest } from 'redux-saga/effects';

import { CaseNoConsentActions, OrderExpiredReportActions } from '../constants';
import { CaseNoConsentApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteCaseNoConsentAction,
    GetCaseNoConsentAction,
    GetCaseNoConsentsAction,
} from '../actions/caseNoConsent-actions';

import sortingService from '../../services/sorting';
import { handleError, resolveApiCall, ResolverApiFailure } from '../../services/api-handlers/api-resolver';
import { groupCaseNoConsentsToCsv } from '../../helpers/groupCaseNoConsentsToCsv';
import { exportToCsv } from '../../services/exports/csv';
import { toast } from 'react-toastify';

function* handleFetchCaseNoConsents(action: GetCaseNoConsentsAction) {
    const oldData = yield select((state: StoreState) => state.casesNoConsent.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        CaseNoConsentApi.getCaseNoConsents({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchCaseNoConsentsCsv(action: GetCaseNoConsentsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            CaseNoConsentApi.getCaseNoConsents({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                const actionFiles: GetCaseNoConsentsAction = {
                    type: OrderExpiredReportActions.FETCH_ORDER_EXPIRED_REPORTS_CSV,
                    payload: {
                        ...filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchCaseNoConsentsCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupCaseNoConsentsToCsv(dataAll);
                    exportToCsv('case-no-Consents.csv', groupCsv);
                } else {
                    const groupCsvData = groupCaseNoConsentsToCsv(data.payload);
                    exportToCsv('case-no-Consents.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchCaseNoConsent(action: GetCaseNoConsentAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => CaseNoConsentApi.getCaseNoConsent(id));
}

function* handleDeleteCaseNoConsent(action: DeleteCaseNoConsentAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => CaseNoConsentApi.deleteCaseNoConsent(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(CaseNoConsentActions.FETCH_CASE_NO_CONSENTS, handleFetchCaseNoConsents);
    yield takeLatest(CaseNoConsentActions.FETCH_CASE_NO_CONSENTS_CSV, handleFetchCaseNoConsentsCsv);
    yield takeLatest(CaseNoConsentActions.FETCH_CASE_NO_CONSENT, handleFetchCaseNoConsent);
    yield takeLatest(CaseNoConsentActions.DELETE_CASE_NO_CONSENT, handleDeleteCaseNoConsent);
}
